import settings from '@/constants/constants';
import useGetOrderDetails from '@/hooks/order/useGetOrderDetails';
import useGetOrderReceipt from '@/hooks/order/useGetOrderReceipt';
import CopyToClipboard from '@montugroup/circuit-shared/components/common/CopyToClipboard';
import { Accordion, AlertTitle, Box, Button, Typography } from '@montugroup/design-system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';

import OrderStatusChip, { getOrderStatusChipProps } from '../OrderStatusChip/OrderStatusChip';
import OrderBreakdown, { getOrderBreakdownProps } from '../order-breakdown/OrderBreakdown';
import OrderBreakdownLoading from '../order-breakdown/OrderBreakdownLoading';
import type { OrderStage } from '../pharmacy-order-details/PharmacyOrderDetails';
import PharmacyOrderDetails from '../pharmacy-order-details/PharmacyOrderDetails';
import PharmacyOrderDetailsLoading from '../pharmacy-order-details/PharmacyOrderDetailsLoading';

import FAQButtons from './FAQButtons';
import {
  ActionText,
  DesktopReceiptButtonContainer,
  DispenseIntervalStyledChip,
  MobileReceiptButtonContainer,
  OrderDetailsContainer,
  OrderInvoiceButtonContainer,
  OrderItemsContainer,
  OrderNumberContainer,
  OrderStatusChipMobile,
  OrderStatusContainer,
  OrderSummaryContainer,
  OrderTrackingButtonContainer,
  PharmacyDetailsContainer,
  ReceiptButton,
  SplitOrderAlert,
  StyledAccordionDetails,
  StyledAccordionSummary,
  Wrapper,
} from './OrderHistoryCard.styles';
import SplitOrderDetails from './SplitOrderDetails';

import type { DeliveryOption } from '@/hooks/patient/useGetPatientOrders';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared/constants/';
import type { IntervalDetails } from '@montugroup/circuit-shared/types/refill';
import LockClock from '@mui/icons-material/LockClock';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { OrderHistoryDispenseIntervalAlert } from './OrderHistoryDispenseIntervalAlert';
import { OrderHistoryPresenceAlert } from './OrderHistoryPresenceAlert';
import { OrderHistoryValidIdAlert } from './OrderHistoryValidIdAlert';

export type OrderHistoryIntervalDetails = {
  estimatedNextProcessDate?: IntervalDetails['estimatedNextProcessDate'];
  estimatedIntervalState?: IntervalDetails['estimatedIntervalState'];
  productIdsInInterval?: number[];
};

export type OrderHistoryCardProps = {
  orderNumber: string;
  receiptLink?: string;
  numberOfItems: number;
  orderDate: Date;
  pharmacy?: {
    name: string;
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zipCode: string;
  };
  orderPrice: string;
  orderStatus: number;
  trackingLink?: string;
  delivered: boolean;
  dispensingFee: string;
  splitOrder?: boolean;
  cancelledDate?: string | null;
  defaultExpanded: boolean;
  intervalDetails?: OrderHistoryIntervalDetails | null;
  deliveryOption?: DeliveryOption;
};

function ViewReceiptButton({ orderCode, expanded }: { orderCode: string; expanded: boolean }) {
  const { data: receiptData, isError } = useGetOrderReceipt(orderCode, expanded);

  if (receiptData && !isError) {
    return (
      <OrderInvoiceButtonContainer>
        <ReceiptButton href={receiptData.signedUrl} fullWidth variant="outlined">
          <Typography
            variant="body1"
            sx={(theme) => ({
              fontWeight: 600,
              padding: theme.spacing(2),
              color: theme.palette.primary.main,
              borderColor: 'var(--mui-palette-primary-_states-disabled)',
            })}
          >
            View receipt
          </Typography>
        </ReceiptButton>
      </OrderInvoiceButtonContainer>
    );
  }

  return null;
}

function ViewReceiptButtonLazy({ orderCode }: { orderCode: string }) {
  const { data: receiptData, isError, refetch, isLoading, error } = useGetOrderReceipt(orderCode, false);
  const errorMessage = (error as any)?.body.error.message;
  const navigate = useNavigate();
  useEffect(() => {
    if (receiptData?.signedUrl) {
      navigate(receiptData!.signedUrl);
    }
  }, [receiptData, navigate]);

  return (
    <OrderInvoiceButtonContainer>
      <ReceiptButton
        disabled={isError}
        onClick={() => refetch()}
        fullWidth
        variant="outlined"
        loading={isLoading}
        loadingPosition="end"
      >
        <Typography
          variant="body1"
          sx={(theme) => ({
            fontWeight: 600,
            padding: theme.spacing(2),
            color: theme.palette.primary.main,
            borderColor: 'var(--mui-palette-primary-_states-disabled)',
          })}
        >
          View receipt
        </Typography>
      </ReceiptButton>
      {isError && errorMessage ? (
        <OrderInvoiceButtonContainer>
          <Alert severity="error">{errorMessage}</Alert>
        </OrderInvoiceButtonContainer>
      ) : null}
    </OrderInvoiceButtonContainer>
  );
}

export function OrderHistoryCard(props: OrderHistoryCardProps) {
  const {
    orderNumber,
    orderDate,
    pharmacy,
    orderStatus,
    delivered,
    trackingLink,
    dispensingFee,
    splitOrder = false,
    cancelledDate,
    defaultExpanded,
    intervalDetails,
    deliveryOption,
  } = props;

  const enableLazyReceipt = useFeatureFlag('enable_lazy_order_receipt');

  const [expanded, setExpanded] = useState(defaultExpanded);
  const orderCancelled = orderStatus === settings.orderStatus.CANCELLED;
  const orderRefunded = orderStatus === settings.orderStatus.REFUNDED;

  const shouldHideBanner =
    orderStatus === settings.orderStatus.COMPLETED || props.delivered || orderCancelled || orderRefunded;

  const {
    data: orderDetails,
    isError: orderDetailsError,
    isLoading: orderDetailsLoading,
  } = useGetOrderDetails(orderNumber, expanded);

  const handleAccordionChange = (_e: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const readableOrderDate = orderDate
    .toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
    .replace(/(\d{2}) (\w{3}) (\d{4})/, '$1 $2 $3');

  const formattedCancelledDate = cancelledDate
    ? new Date(cancelledDate)
        .toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })
        .replace(/(\d{2}) (\w{3}) (\d{4})/, '$1 $2 $3')
    : null;

  const renderReceiptButton = orderDetails && !orderDetailsError && !orderCancelled;

  const dispenseInterval =
    intervalDetails?.estimatedIntervalState && intervalDetails.estimatedIntervalState !== 'AVAILABLE';

  const orderStatusChipProps = getOrderStatusChipProps({
    status: orderStatus,
    delivered,
    shouldRenderSplitOrderDetails: splitOrder,
    isClickAndCollectOrder: Boolean(deliveryOption?.name === DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name),
  });

  const orderStatusChip =
    dispenseInterval && !splitOrder ? (
      <DispenseIntervalStyledChip label="Dispense interval" icon={<LockClock color="info" />} />
    ) : (
      <OrderStatusChip {...orderStatusChipProps} />
    );

  const shouldShowValidIdAlert = deliveryOption?.deliveryRequirements?.identification && !shouldHideBanner;

  const shouldShowRequiresPresenceAlert =
    !shouldShowValidIdAlert && deliveryOption?.deliveryRequirements.presence && !shouldHideBanner;

  return (
    <Wrapper>
      <Accordion defaultExpanded={defaultExpanded} onChange={handleAccordionChange} elevation={0}>
        <StyledAccordionSummary>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <OrderNumberContainer>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Order #{orderNumber}
                  </Typography>
                  <CopyToClipboard textToCopy={orderNumber} buttonVariant="icon" size="small" />
                  <OrderStatusContainer data-dd-privacy="allow">{orderStatusChip}</OrderStatusContainer>
                </OrderNumberContainer>
                <OrderDetailsContainer>
                  <Typography variant="body2" data-chromatic="ignore">
                    Placed on {readableOrderDate}
                  </Typography>
                </OrderDetailsContainer>
                {orderCancelled ? (
                  <OrderDetailsContainer>
                    <Typography variant="body2" data-chromatic="ignore">
                      Cancelled on {formattedCancelledDate}
                    </Typography>
                  </OrderDetailsContainer>
                ) : null}
                <OrderStatusChipMobile data-dd-privacy="allow">{orderStatusChip}</OrderStatusChipMobile>
              </Box>
              <Box>
                <KeyboardArrowDownIcon
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                />
              </Box>
            </Box>

            {dispenseInterval && !splitOrder && (
              <OrderHistoryDispenseIntervalAlert intervalDetails={intervalDetails} orderNumber={orderNumber} />
            )}
          </Box>
        </StyledAccordionSummary>

        {shouldShowValidIdAlert && (
          <StyledAccordionDetails>
            <OrderHistoryValidIdAlert deliveryOptionName={deliveryOption.name} />
          </StyledAccordionDetails>
        )}

        {shouldShowRequiresPresenceAlert && (
          <StyledAccordionDetails>
            <OrderHistoryPresenceAlert deliveryOptionName={deliveryOption.name} />
          </StyledAccordionDetails>
        )}

        <StyledAccordionDetails>
          <OrderSummaryContainer>
            {splitOrder && (
              <SplitOrderAlert security="warning" icon={<WarningAmberOutlined color="warning" />}>
                <AlertTitle sx={(theme) => ({ color: theme.palette.warning.dark })}>
                  Items in this order are being delivered separately
                </AlertTitle>
                <Typography
                  variant="body2"
                  fontWeight="semibold"
                  sx={(theme) => ({ marginTop: theme.spacing(2), color: theme.palette.warning.dark })}
                >
                  This order is being delivered in separate shipments. Please check the status of each split order below
                  to track their delivery progress.{' '}
                </Typography>
              </SplitOrderAlert>
            )}
            {!orderCancelled &&
              !orderRefunded &&
              (splitOrder && orderDetails ? (
                <SplitOrderDetails
                  orderDetails={orderDetails}
                  isLoading={orderDetailsLoading}
                  intervalDetails={intervalDetails}
                  trackingLink={trackingLink}
                />
              ) : (
                <PharmacyDetailsContainer>
                  {orderDetailsLoading && <PharmacyOrderDetailsLoading />}
                  {!orderDetailsLoading && orderDetails && !orderDetailsError && (
                    <PharmacyOrderDetails
                      pharmacy={pharmacy}
                      fullOrderCode={orderDetails[0].order_code}
                      stage={orderStatusChipProps.status as OrderStage}
                      deliveryOption={deliveryOption}
                    />
                  )}
                  {trackingLink && (
                    <OrderTrackingButtonContainer>
                      <Button href={trackingLink} fullWidth variant="outlined">
                        <ActionText variant="body1">Track delivery</ActionText>
                      </Button>
                    </OrderTrackingButtonContainer>
                  )}
                </PharmacyDetailsContainer>
              ))}

            <OrderItemsContainer>
              {!orderDetailsLoading && orderDetails ? (
                <OrderBreakdown
                  {...getOrderBreakdownProps({
                    dispensingFee: Number(dispensingFee),
                    orderDetails,
                    isRefundedOrCancelled: orderCancelled || orderRefunded,
                    splitOrder,
                  })}
                />
              ) : (
                <OrderBreakdownLoading />
              )}

              {renderReceiptButton && (
                <DesktopReceiptButtonContainer>
                  {enableLazyReceipt ? (
                    <ViewReceiptButtonLazy orderCode={orderDetails[0].order_code} />
                  ) : (
                    <ViewReceiptButton orderCode={orderDetails[0].order_code} expanded={expanded} />
                  )}
                </DesktopReceiptButtonContainer>
              )}
            </OrderItemsContainer>
          </OrderSummaryContainer>

          {renderReceiptButton && (
            <MobileReceiptButtonContainer>
              {enableLazyReceipt ? (
                <ViewReceiptButtonLazy orderCode={orderDetails[0].order_code} />
              ) : (
                <ViewReceiptButton orderCode={orderDetails[0].order_code} expanded={expanded} />
              )}
            </MobileReceiptButtonContainer>
          )}

          <FAQButtons
            isClickAndCollect={deliveryOption?.name === DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name}
          />
        </StyledAccordionDetails>
      </Accordion>
    </Wrapper>
  );
}

export default OrderHistoryCard;
